/**
 * CustomElementPatcher
 *
 * Polyfill pour les "customized built-in elements" (pattern <a is="my-link">,
 * <button is="my-button">, etc.) que Safari < 16.4 (mars 2023) ne supportait
 * pas nativement. Sur ces browsers, le moteur instancie l'élément en HTMLAnchorElement
 * "vide" sans appeler le constructeur de la sous-classe — d'où le besoin de
 * `setPrototypeOf` + `init()` manuel pour rétablir le comportement attendu.
 *
 * Optimisations vs version Nextsky d'origine (mesure baseline Lighthouse :
 * 429ms scripting sur fiche produit) :
 *
 * 1. Feature detection synchrone au constructor : si le browser supporte
 *    nativement les customized built-in elements, TOUT le code de patching
 *    devient un no-op. Concrètement Chrome (toujours supporté), Firefox 63+,
 *    Edge (toujours), Safari 16.4+ tombent dans le chemin no-op. Ne reste
 *    actif que pour Safari ≤16.3 dont la part de marché est résiduelle (et
 *    qui finira par disparaître naturellement).
 *
 * 2. MutationObserver ciblé : la version d'origine appelait
 *    `document.querySelectorAll(selector)` sur TOUT le document à CHAQUE
 *    mutation, peu importe ce qui avait changé. La version optimisée
 *    n'inspecte QUE les nœuds réellement ajoutés (mutation.addedNodes) et
 *    leurs descendants. Sur fiche produit où il y a beaucoup de mutations
 *    DOM (variant swatches, lazy loading, motion-effect, modals…), gain
 *    proportionnel au nombre d'éléments dans le document × nombre de
 *    mutations.
 *
 * Le module reste backward-compatible : même API publique
 * (CustomElement.observeAndPatchCustomElements / patchAllCustomElements /
 * patchCustomElement) consommée par theme.js, media-gallery.js, facets.js.
 */

class CustomElementPatcher {
  constructor() {
    this._nativeSupport = this._detectNativeSupport();
  }

  // Modern browsers (Safari 16.4+, Chrome, Firefox 63+, Edge) instancient
  // correctement les `customElements.define(..., { extends: 'tag' })`. On le
  // détecte une fois au boot via une définition jetable + vérification que
  // l'élément créé est bien instance de la classe étendue.
  _detectNativeSupport() {
    try {
      const tag = `__cep_test_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      class _Probe extends HTMLAnchorElement {}
      customElements.define(tag, _Probe, { extends: "a" });
      const el = document.createElement("a", { is: tag });
      return el instanceof _Probe;
    } catch (_) {
      return false;
    }
  }

  patchCustomElement(tagElement, defineElement, classElement) {
    if (this._nativeSupport) return;
    const selector = `${tagElement}[is="${defineElement}"]`;
    document.querySelectorAll(selector).forEach((el) => {
      this._patchOne(el, classElement);
    });
  }

  patchAllCustomElements(elements = {}) {
    if (this._nativeSupport) return;
    Object.entries(elements).forEach(
      ([defineElement, { tagElement, classElement }]) => {
        this.patchCustomElement(tagElement, defineElement, classElement);
      },
    );
  }

  observeAndPatchCustomElements(elements = {}) {
    if (this._nativeSupport) return;

    // Initial pass — patch les éléments présents au load.
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () =>
        this.patchAllCustomElements(elements),
      );
    } else {
      this.patchAllCustomElements(elements);
    }

    // Pré-compile les sélecteurs une fois pour éviter les concaténations
    // dans la hot path du MutationObserver.
    const targets = Object.entries(elements).map(
      ([defineElement, { tagElement, classElement }]) => ({
        defineElement,
        tagElement,
        classElement,
        selector: `${tagElement}[is="${defineElement}"]`,
      }),
    );

    // Observer ciblé : on inspecte uniquement les nœuds ajoutés par la
    // mutation, pas tout le document. Pour chaque addedNode, on teste s'il
    // matche un sélecteur ET on parcourt ses descendants — ce qui est borné
    // par la taille du sous-arbre ajouté, pas du document.
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type !== "childList") continue;
        for (const node of m.addedNodes) {
          if (node.nodeType !== 1) continue; // Element only
          for (const t of targets) {
            if (node.matches?.(t.selector)) {
              this._patchOne(node, t.classElement);
            }
            const matches = node.querySelectorAll?.(t.selector);
            if (matches) {
              matches.forEach((el) => this._patchOne(el, t.classElement));
            }
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    // En designMode (preview Shopify), un editor add/remove/move des sections
    // entières via des shopify:* events. On reprend une passe complète pour
    // ces cas là — ils sont rares (action manuelle utilisateur).
    if (window.Shopify && Shopify.designMode) {
      const fullPatch = () => this.patchAllCustomElements(elements);
      [
        "shopify:section:load",
        "shopify:section:select",
        "shopify:section:deselect",
        "shopify:block:select",
        "shopify:block:deselect",
      ].forEach((ev) => document.addEventListener(ev, fullPatch));
    }
  }

  _patchOne(el, classElement) {
    if (!(el instanceof classElement)) {
      Object.setPrototypeOf(el, classElement.prototype);
      if (typeof el.init === "function") {
        el.init();
      }
    }
  }
}

export const CustomElement = new CustomElementPatcher();
